import { useState } from "react";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { buildWhatsAppLink } from "../lib/contact";

const FLEET = [
    {
        id: "12t",
        capacity: "12",
        unit: "Ton",
        title: "Mobile Crane",
        image: "https://images.unsplash.com/photo-1748974467633-2e21ffd9dd94",
        specs: [
            { k: "Boom", v: "24 m" },
            { k: "Type", v: "Hydraulic" },
            { k: "Best for", v: "City sites, factories" },
        ],
    },
    {
        id: "25t",
        capacity: "25",
        unit: "Ton",
        title: "Mobile Crane",
        image: "https://images.pexels.com/photos/29502193/pexels-photo-29502193.jpeg",
        specs: [
            { k: "Boom", v: "32 m" },
            { k: "Type", v: "Truck Mounted" },
            { k: "Best for", v: "Construction & infra" },
        ],
    },
    {
        id: "50t",
        capacity: "50",
        unit: "Ton",
        title: "Heavy Mobile Crane",
        image: "https://images.unsplash.com/photo-1771679910145-3a368d7f537c",
        specs: [
            { k: "Boom", v: "42 m" },
            { k: "Type", v: "Hydraulic" },
            { k: "Best for", v: "Plant equipment lifts" },
        ],
    },
    {
        id: "100t",
        capacity: "100+",
        unit: "Ton",
        title: "Crawler / Hydraulic",
        image: "https://images.pexels.com/photos/29502213/pexels-photo-29502213.jpeg",
        specs: [
            { k: "Boom", v: "60 m+" },
            { k: "Type", v: "Crawler" },
            { k: "Best for", v: "Refineries, steel, energy" },
        ],
    },
];

export const Fleet = () => {
    const [hovered, setHovered] = useState(null);
    return (
        <section
            id="fleet"
            data-testid="fleet-section"
            className="relative py-24 lg:py-32 bg-[#0F0F0F]"
        >
            <div className="max-w-7xl mx-auto px-5 lg:px-8">
                {/* Header */}
                <div className="reveal flex flex-col gap-5 mb-14 lg:mb-20">
                    <div className="flex items-center gap-3">
                        <span className="w-10 h-[2px] bg-[#FFC107]" />
                        <span className="font-body text-xs uppercase tracking-[0.32em] text-[#FFC107]">
                            Our Fleet
                        </span>
                    </div>
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                        <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase text-white leading-[0.9] max-w-3xl">
                            From 12 Tonnes To{" "}
                            <span className="text-[#FFC107]">100+</span>.
                            <br />
                            <span className="text-neutral-500">
                                Whatever The Lift.
                            </span>
                        </h2>
                        <p className="font-body text-neutral-400 text-base lg:text-lg leading-relaxed max-w-sm">
                            A versatile, well-maintained fleet of mobile,
                            hydraulic and crawler cranes — operated by licensed,
                            trained crews.
                        </p>
                    </div>
                </div>

                {/* Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                    {FLEET.map((c, idx) => (
                        <article
                            key={c.id}
                            data-testid={`fleet-card-${c.id}`}
                            onMouseEnter={() => setHovered(c.id)}
                            onMouseLeave={() => setHovered(null)}
                            className="reveal group relative bg-[#171717] border border-white/10 hover:border-[#FFC107]/60 transition-all duration-500 flex flex-col"
                            style={{ animationDelay: `${idx * 0.08}s` }}
                        >
                            {/* Image */}
                            <div className="relative aspect-[4/3] overflow-hidden bg-[#0A0A0A]">
                                <img
                                    src={c.image}
                                    alt={`${c.capacity} Ton ${c.title}`}
                                    loading="lazy"
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />

                                {/* Capacity badge */}
                                <div className="absolute bottom-4 left-4 flex items-baseline gap-1.5">
                                    <span className="font-display text-6xl text-[#FFC107] leading-none drop-shadow-[0_2px_0_rgba(0,0,0,0.5)]">
                                        {c.capacity}
                                    </span>
                                    <span className="font-body text-xs uppercase tracking-[0.2em] text-white">
                                        {c.unit}
                                    </span>
                                </div>

                                {/* Hover indicator */}
                                <div className="absolute top-4 right-4 w-10 h-10 bg-[#FFC107] flex items-center justify-center opacity-0 -translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                                    <ArrowUpRight size={20} className="text-black" strokeWidth={2.5} />
                                </div>
                            </div>

                            {/* Body */}
                            <div className="p-6 flex flex-col flex-1">
                                <h3 className="font-display text-2xl uppercase text-white tracking-wide mb-4">
                                    {c.title}
                                </h3>
                                <dl className="flex flex-col divide-y divide-white/5 mb-6">
                                    {c.specs.map((s) => (
                                        <div
                                            key={s.k}
                                            className="flex items-center justify-between py-2"
                                        >
                                            <dt className="font-body text-[11px] uppercase tracking-[0.18em] text-neutral-500">
                                                {s.k}
                                            </dt>
                                            <dd className="font-body text-sm text-neutral-200 text-right">
                                                {s.v}
                                            </dd>
                                        </div>
                                    ))}
                                </dl>
                                <a
                                    href={buildWhatsAppLink(
                                        `Hi, I'd like to book the ${c.capacity} Ton ${c.title}. Please share availability.`,
                                    )}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`fleet-book-${c.id}`}
                                    className="mt-auto inline-flex items-center justify-center gap-2 border border-white/15 hover:border-[#FFC107] hover:bg-[#FFC107] hover:text-black text-white font-body text-xs uppercase tracking-[0.2em] py-3 transition-all duration-200"
                                >
                                    <MessageCircle size={14} strokeWidth={2.5} />
                                    Book this Crane
                                </a>
                            </div>

                            {/* Subtle hovered marker line */}
                            <div
                                className={`absolute left-0 top-0 h-full w-[3px] bg-[#FFC107] origin-top transition-transform duration-500 ${
                                    hovered === c.id ? "scale-y-100" : "scale-y-0"
                                }`}
                            />
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
};
